export const TV_INSET_KEY = 'tv-acceptance-inset-v1';
export const TV_REPORT_KEY = 'tv-acceptance-report-v1';
export const TV_DISPLAY_SAMPLE_KEY = 'tv-acceptance-display-sample-v1';

export function tvAcceptanceEnabled(environment: string | undefined) {
  return environment === 'staging' || environment === 'local';
}

export const isTvAcceptanceEnabled = tvAcceptanceEnabled(__DEPLOY_ENV__);

export function normalizeInset(value: unknown): number {
  if (typeof value !== 'number' && typeof value !== 'string') return 0;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 && number <= 10 ? Math.round(number) : 0;
}

type PreferenceStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function readTvInset(storage?: PreferenceStorage): number {
  try {
    return normalizeInset((storage ?? window.localStorage).getItem(TV_INSET_KEY));
  } catch {
    return 0;
  }
}

export function writeTvInset(inset: number, storage?: PreferenceStorage): boolean {
  try {
    const target = storage ?? window.localStorage;
    const value = String(normalizeInset(inset));
    target.setItem(TV_INSET_KEY, value);
    return target.getItem(TV_INSET_KEY) === value;
  } catch {
    return false;
  }
}

/** Explicit allowlist: never include URL, pairing credentials, cookies or storage contents. */
export function collectTvDiagnostics() {
  const viewport = window.visualViewport;
  const root = document.querySelector('[data-tv-canvas]')?.getBoundingClientRect();
  return {
    capturedAt: new Date().toISOString(),
    environment: __DEPLOY_ENV__,
    webRevision: __GIT_REVISION__ ?? 'development',
    userAgent: navigator.userAgent,
    language: navigator.language,
    screen: {
      width: screen.width,
      height: screen.height,
      availWidth: screen.availWidth,
      availHeight: screen.availHeight,
      colorDepth: screen.colorDepth,
    },
    window: { width: innerWidth, height: innerHeight, devicePixelRatio: devicePixelRatio || 1 },
    visualViewport: viewport
      ? {
          width: viewport.width,
          height: viewport.height,
          scale: viewport.scale,
          offsetLeft: viewport.offsetLeft,
          offsetTop: viewport.offsetTop,
        }
      : null,
    canvas: root ? { x: root.x, y: root.y, width: root.width, height: root.height } : null,
    fullscreen: Boolean(
      document.fullscreenElement ||
      (document as unknown as { webkitFullscreenElement?: Element }).webkitFullscreenElement,
    ),
    capabilities: {
      dvh: typeof CSS !== 'undefined' && CSS.supports('height', '100dvh'),
      resizeObserver: typeof ResizeObserver !== 'undefined',
    },
    online: navigator.onLine,
    savedInsetPercent: readTvInset(),
  };
}

export function saveDisplaySample() {
  try {
    localStorage.setItem(TV_DISPLAY_SAMPLE_KEY, JSON.stringify(collectTvDiagnostics()));
  } catch {
    /* Optional sample; never block navigation. */
  }
}

export function readDisplaySample(): ReturnType<typeof collectTvDiagnostics> | null {
  try {
    const raw = localStorage.getItem(TV_DISPLAY_SAMPLE_KEY);
    if (!raw || raw.length > 16000) return null;
    const sample = JSON.parse(raw);
    // Only samples produced by this collector have this identity and geometry.
    if (
      sample.environment !== __DEPLOY_ENV__ ||
      typeof sample.capturedAt !== 'string' ||
      !sample.canvas
    )
      return null;
    // Reconstruct the allowlist so an old or foreign value cannot add arbitrary fields.
    const template = collectTvDiagnostics();
    const pick = (shape: unknown, value: unknown): unknown => {
      if (shape === null) return null;
      if (typeof shape === 'number')
        return typeof value === 'number' && Number.isFinite(value) ? value : 0;
      if (typeof shape === 'boolean') return value === true;
      if (typeof shape === 'string') return typeof value === 'string' ? value.slice(0, 2048) : '';
      return Object.fromEntries(
        Object.entries(shape as object).map(([key, child]) => [
          key,
          pick(child, (value as Record<string, unknown> | null)?.[key]),
        ]),
      );
    };
    // The acceptance page has no canvas; supply a geometry template for the prior display.
    template.canvas = { x: 0, y: 0, width: 0, height: 0 };
    return pick(template, sample) as ReturnType<typeof collectTvDiagnostics>;
  } catch {
    return null;
  }
}
